/* --------------------------------------------------------------------------
   useForecastApi.js – composable to retrieve forecast data from the Python
   Azure-Functions back-end.

   Interface:  const { loading, data, error, load } = useForecast();

   • GET /r3moteio_ai/forecast?operatorId=…&chemical=…&horizonDays=…
   • Simple in-memory cache to avoid re-fetching identical parameter sets
--------------------------------------------------------------------------- */

import { ref } from 'vue';
import { getEnv } from '@/env-runtime';

// Resolve Functions base URL (build-time or runtime)
let API_BASE = (
  getEnv('VITE_FUNCTIONS_BASE') ||
  getEnv('VUE_APP_FUNCTIONS_BASE') ||
  getEnv('FUNCTIONS_BASE') ||
  ''
).replace(/\/$/, '');

if (!API_BASE) {
  console.warn('[useForecastApi] FUNCTIONS_BASE env missing; defaulting to local proxy (/api).');
  API_BASE = '/api';
}

const FUNC_KEY = (
  getEnv('VITE_FUNCTIONS_KEY') ||
  getEnv('VUE_APP_FUNCTIONS_KEY') ||
  getEnv('VITE_AZ_FN_KEY') ||
  getEnv('VUE_APP_AZ_FN_KEY') ||
  getEnv('FUNCTIONS_KEY') ||
  ''
).trim();

function buildHeaders() {
  return {
    ...(FUNC_KEY ? { 'x-functions-key': FUNC_KEY } : {}),
    Accept: 'application/json',
  };
}

function withCodeParam(path) {
  return FUNC_KEY ? `${path}${path.includes('?') ? '&' : '?'}code=${encodeURIComponent(FUNC_KEY)}` : path;
}

// ---------------------------------------------------------------------------
// Singleton composable – ensures every caller shares the same refs, cache and
// therefore the same fetched data.
// ---------------------------------------------------------------------------

let sharedInstance = null;

export function useForecast() {
  if (sharedInstance) return sharedInstance;

  const loading = ref(false);
  const data = ref(null);
  const error = ref(null);

  // Simple session-scope cache  (key = "operatorId:chemical:horizon")
  const cache = new Map();

  async function load(operatorId, chemical, horizonDays = 365, scopeType = null, scopeId = null) {
    if (!operatorId || !chemical) return;  // guard-clause

    let key = `${operatorId}:${chemical.toLowerCase()}:${horizonDays}`;
    if (scopeType && scopeId) {
      key += `:${scopeType}:${scopeId}`;
    }

    error.value = null;

    if (cache.has(key)) {
      data.value = cache.get(key);
      return;
    }

    loading.value = true;
    data.value = null; // Clear stale data
    try {
      const params = {
        operatorId: operatorId.toString(),
        chemical,
        horizonDays: horizonDays.toString(),
      };

      if (scopeType && scopeId) {
        params.scopeType = scopeType;
        params.scopeId = scopeId.toString();
      }

      const qs = new URLSearchParams(params);

      const url = withCodeParam(`${API_BASE}/r3moteio_ai/forecast?${qs}`);
      const resp = await fetch(url, {
        headers: buildHeaders(),
      });

      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const json = await resp.json();
      cache.set(key, json);
      data.value = json;
    } catch (err) {
      error.value = err?.message ?? String(err);
      console.error('[useForecast] load() failed:', err);
    } finally {
      loading.value = false;
    }
  }

  sharedInstance = { loading, data, error, load };
  return sharedInstance;
}
